import styled from "@emotion/styled";
import { CSSProperties, FC, PropsWithChildren } from "react";
import { Modal } from "../Modal";
import { useDialogTriggerOpenState } from "../dialog/useDialogTriggerOpenState";
import { IconDialogClose } from "../../../icons/IconDialogClose"; // Don't change this. `icons` folder is outside `src` -> results in a type check error without the dots
import { CustomDialogCloseButton } from "./CustomDialogCloseButton";

// Positioning context for the absolutely-placed close button. `Modal` (alert
// variant) supplies the overlay, centering, animation and z-index stacking.
const Body = styled.div`
  label: CustomDialogBody;
  position: relative;
`;

/**
 * Drives the alert card to the CustomDialog look (342px white rounded card).
 * `--popup-dialog-width` is set on the overlay so it cascades to the card and
 * to consumers that read it for their own layout math (e.g. the Lyra popup
 * background image), preserving the previous public styling contract.
 */
const MODAL_STYLE = {
  "--dialog-background": "var(--color-white)",
  "--border-radius": "1.5rem",
  "--popup-dialog-width": "21.375rem" /* 342/16 */,
  "--width": "var(--popup-dialog-width)",
  "--max-width": "var(--popup-dialog-width)"
} as CSSProperties;

type CustomDialogProps = PropsWithChildren<{
  /** Retained for API compatibility — the alert Modal always animates. */
  animated?: boolean;
  /** Default true. When false, neither backdrop click nor Escape dismisses. */
  isDismissable?: boolean;
  /**
   * Controlled open state. Optional — when rendered inside a
   * `CustomDialogTrigger`/`PopupDialogTrigger` the open state comes from the
   * trigger instead.
   */
  isOpen?: boolean;
  /** Called after the dismiss animation completes. */
  onClose?: () => void;
  /** Forwarded to the card, so `styled(CustomDialog)` overrides keep working. */
  className?: string;
  /** Accessible label for the dialog. */
  ariaLabel?: string;
}>;

export const CustomDialog: FC<CustomDialogProps> = ({
  children,
  isDismissable = true,
  isOpen: controlledIsOpen,
  onClose,
  className,
  ariaLabel = "Dialog"
}) => {
  const { isOpen, handleDidDismiss } = useDialogTriggerOpenState({
    isOpen: controlledIsOpen,
    onClose
  });

  return (
    <Modal
      isOpen={isOpen}
      variant="alert"
      backdropDismiss={isDismissable}
      onDidDismiss={handleDidDismiss}
      className={className}
      style={MODAL_STYLE}
      ariaLabel={ariaLabel}
    >
      <Body>
        <CustomDialogCloseButton slot="close" aria-label="Close">
          <IconDialogClose />
        </CustomDialogCloseButton>
        {children}
      </Body>
    </Modal>
  );
};
